import {useMemo} from 'react';
import {matchPath, useLocation} from 'react-router-dom';
import {useQueries} from '@tanstack/react-query';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getMasterDataApi} from '@/matic/features/master-data/api/master-data-api';
import {masterDataKeys} from '@/matic/features/master-data/query-keys';
import {useObjectTypes} from '@/matic/features/master-data/hooks/useObjectTypes';
import {
  isCategoryScopeItem,
  parseScopeParam,
} from '@/matic/features/relay/lib/scope-utils';
import type {CreateDialogContextAssociation} from '../components/library/RelayCreateDialog';

/** Route pattern for an individual object record's detail page — the other
 *  place (besides /library) a user has an obvious single-record context. */
const OBJECT_RECORD_ROUTE =
  '/admin/master-data/object-types/:typeId/records/:recordId';

/**
 * Resolves the current page's implied record scope into pre-filled
 * association suggestions for the global create/quick-drop entry point:
 *  - On /library with an active scope: every scoped record.
 *  - On an object record's own detail page: that one record (the page's own
 *    header already names it — Create/quick-drop should use the same context
 *    instead of asking again).
 *  - Everywhere else: [] — the created item / dropped file lands unassociated.
 * Queries are cache hits when the owning page (LibraryContainer /
 * ObjectRecordDetailContainer) is mounted.
 */
export function useLibraryScopeContextAssociations(): CreateDialogContextAssociation[] {
  const {pathname, search} = useLocation();
  const tenantId = useRequiredTenantId();

  const isLibrary = pathname.startsWith('/library');
  const objectRecordMatch = matchPath(OBJECT_RECORD_ROUTE, pathname);
  const hasContext = isLibrary || objectRecordMatch !== null;

  const scopeParam = isLibrary
    ? (new URLSearchParams(search).get('scope') ?? '')
    : '';

  const scopeItems = useMemo(() => {
    if (isLibrary) return parseScopeParam(scopeParam);
    if (objectRecordMatch?.params.typeId && objectRecordMatch.params.recordId) {
      return [
        {
          typeId: objectRecordMatch.params.typeId,
          recordId: objectRecordMatch.params.recordId,
        },
      ];
    }

    return [];
  }, [isLibrary, scopeParam, objectRecordMatch]);

  const uniqueTypeIds = useMemo(
    () => [...new Set(scopeItems.map((i) => i.typeId))],
    [scopeItems]
  );

  const {data: typesData} = useObjectTypes();
  const types = useMemo(() => typesData?.items ?? [], [typesData]);

  const api = getMasterDataApi();
  const recordQueries = useQueries({
    queries: uniqueTypeIds.map((typeId) => ({
      queryKey: masterDataKeys.objectRecords(typeId, tenantId, undefined),
      queryFn: async () => api.listObjectRecords(typeId),
      enabled: hasContext && uniqueTypeIds.length > 0,
      staleTime: 60_000,
    })),
  });

  return useMemo(() => {
    if (!hasContext || scopeItems.length === 0) return [];
    const typeMap = Object.fromEntries(types.map((t) => [t.id, t]));
    return scopeItems.flatMap((item): CreateDialogContextAssociation[] => {
      const type = typeMap[item.typeId];
      if (!type) return [];
      if (isCategoryScopeItem(item)) {
        return [
          {
            objectTypeId: item.typeId,
            objectTypeName: type.name,
            objectRecordId: null,
            objectRecordLabel: type.name,
          },
        ];
      }

      const typeIdx = uniqueTypeIds.indexOf(item.typeId);
      const records = recordQueries[typeIdx]?.data?.items ?? [];
      const record = records.find((r) => r.id === item.recordId);
      if (!record) return [];
      return [
        {
          objectTypeId: item.typeId,
          objectTypeName: type.name,
          objectRecordId: item.recordId,
          objectRecordLabel: record.display ?? record.code,
        },
      ];
    });
  }, [hasContext, scopeItems, types, recordQueries, uniqueTypeIds]);
}
